import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { db, addDocument } from '../services/firebase'
import { collection, addDoc } from 'firebase/firestore'
import { motion } from 'framer-motion'
import { getCurrentPosition, getCityFromCoords } from '../utils/helpers'
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  TrophyIcon,
  CurrencyEuroIcon,
  ClockIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import it from 'date-fns/locale/it'
import 'react-datepicker/dist/react-datepicker.css'

const CreateMatch = () => {
  const { t, i18n } = useTranslation()
  const { user, userData, loading: authLoading } = useAuth()
  const dateLocale = i18n.language?.startsWith('it') ? it : undefined
  const navigate = useNavigate()
  const timePickerRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false)
  const [timeDraft, setTimeDraft] = useState({ hour: '', minute: '' })
  const [gettingCoords, setGettingCoords] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')
  const [locationResults, setLocationResults] = useState([])
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    sport: 'calcio',
    matchType: '5v5',
    date: '',
    time: '',
    location: '',
    address: '',
    latitude: null,
    longitude: null,
    city: '',
    duration: 90,
    price: 0,
    description: '',
    maxPlayers: 10,
    equipment: ''
  })

  const selectedDate = useMemo(() => {
    if (!formData.date) return null
    const parsed = new Date(`${formData.date}T00:00:00`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }, [formData.date])

  const hours = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
  }, [])

  const minutes = ['00', '15', '30', '45']

  const getDisplayTime = (time24) => {
    if (!time24) return ''
    const [hh = '', mm = ''] = time24.split(':')
    if (!hh || !mm) return time24
    return `${hh}:${mm}`
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setIsTimePickerOpen(false)
      }
      
      const locationInput = document.querySelector('input[name="address"]')
      const dropdown = document.querySelector('.location-dropdown')
      if (locationInput && dropdown && !locationInput.contains(event.target) && !dropdown.contains(event.target)) {
        setShowLocationDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const extractCityFromAddress = (address) => {
    if (!address || !address.includes(',')) return ''
    const parts = address.split(',').map(p => p.trim()).filter(Boolean)
    if (parts.length === 0) return ''
    const last = parts[parts.length - 1]
    if (/\d/.test(last) && parts.length > 1) {
      return parts[parts.length - 2]
    }
    return last
  }

  const openTimePicker = () => {
    if (formData.time) {
      const [hh = '', minute = ''] = formData.time.split(':')
      setTimeDraft({
        hour: hh,
        minute
      })
    } else {
      setTimeDraft({ hour: '', minute: '' })
    }
    setIsTimePickerOpen(true)
  }

  const applyTimeSelection = () => {
    if (!timeDraft.hour || !timeDraft.minute) return

    setFormData(prev => ({
      ...prev,
      time: `${timeDraft.hour}:${timeDraft.minute}`
    }))
    setIsTimePickerOpen(false)
  }

  const sportOptions = {
    calcio: {
      types: ['5v5', '6v6', '7v7', '8v8', '11v11'],
      players: { '5v5': 10, '6v6': 12, '7v7': 14, '8v8': 16, '11v11': 22 }
    },
    padel: {
      types: ['2v2'],
      players: { '2v2': 4 }
    }
  }

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">{t('createMatch.loading')}</p>
      </div>
    )
  }

  // Redirect if no user or user data
  if (!user || !userData) {
    navigate('/login')
    return null
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    if (name === 'sport') {
      const defaultType = sportOptions[value].types[0]
      setFormData(prev => ({
        ...prev,
        sport: value,
        matchType: defaultType,
        maxPlayers: sportOptions[value].players[defaultType]
      }))
    }
    
    if (name === 'matchType') {
      setFormData(prev => ({
        ...prev,
        matchType: value,
        maxPlayers: sportOptions[formData.sport].players[value]
      }))
    }
  }

  const handleGetCoords = async () => {
    setGettingCoords(true)
    try {
      const position = await getCurrentPosition()
      const city = await getCityFromCoords(position.latitude, position.longitude)
      setFormData(prev => ({
        ...prev,
        latitude: position.latitude,
        longitude: position.longitude,
        city: city || prev.city
      }))
      toast.success(t('location.coordinatesDetected'))
    } catch (error) {
      console.error('Error getting coordinates:', error)
      toast.error(t('location.errorGettingLocation'))
    } finally {
      setGettingCoords(false)
    }
  }

  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setLocationResults([])
      return
    }
    
    setIsSearchingLocation(true)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5&accept-language=en`
      )
      const data = await response.json()
      setLocationResults(data)
      setShowLocationDropdown(true)
    } catch (error) {
      console.error('Error searching location:', error)
      setLocationResults([])
    } finally {
      setIsSearchingLocation(false)
    }
  }

  const handleLocationSearchChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, address: value }))
    setLocationSearch(value)
    
    const debounceTimer = setTimeout(() => {
      searchLocation(value)
    }, 300)
    
    return () => clearTimeout(debounceTimer)
  }

  const selectLocation = (result) => {
    const { lat, lon, display_name } = result
    const addressParts = display_name.split(', ')
    const city = result.address?.city || result.address?.town || result.address?.village || result.address?.municipality || addressParts[0]
    
    setFormData(prev => ({
      ...prev,
      address: display_name,
      latitude: parseFloat(lat),
      longitude: parseFloat(lon),
      city: city || prev.city
    }))
    setLocationSearch(display_name)
    setLocationResults([])
    setShowLocationDropdown(false)
    toast.success(t('location.locationSelected'))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Ensure userData exists before accessing properties
      if (!userData) {
        throw new Error('User data not available')
      }

      // Validate required fields
      if (!formData.date || !formData.time) {
        throw new Error('Please select date and time')
      }

      if (!String(formData.location || '').trim()) {
        throw new Error(t('createMatch.fullAddress', { defaultValue: 'Full Address' }))
      }

      if (!String(formData.address || '').trim()) {
        // In this form, `address` is the "City / location search" input shown to the user.
        throw new Error(t('auth.city', { defaultValue: 'City' }))
      }

      const matchData = {
        ...formData,
        organizerId: user.uid,
        organizerName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        organizerImage: userData.profileImage || '',
        date: new Date(`${formData.date}T${formData.time}`).toISOString(),
        currentPlayers: 1,
        participants: [{
          userId: user.uid,
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          role: userData.role || '',
          profileImage: userData.profileImage || '',
          status: 'accepted',
          joinedAt: new Date().toISOString()
        }],
        status: 'upcoming',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        city: formData.city || extractCityFromAddress(formData.address) || extractCityFromAddress(formData.location)
      }

      // Use addDoc to create a new document with auto-generated ID
      const matchesCollection = collection(db, 'matches')
      const docRef = await addDoc(matchesCollection, matchData)
      
      if (!docRef || !docRef.id) {
        throw new Error(t('createMatch.createError', { defaultValue: 'Failed to create match' }))
      }
      
      toast.success('Match created successfully! 🎉')
      navigate(`/match/${docRef.id}`)
    } catch (error) {
      console.error('Error creating match:', error)
      toast.error(error.message || 'Error creating match')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sport-primary to-sport-secondary p-6 text-white">
          <h1 className="text-2xl font-bold">{t('matches.create')}</h1>
          <p className="opacity-90 mt-1">{t('createMatch.subtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createMatch.titleLabel')} *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="input-modern"
              placeholder={t('createMatch.titlePlaceholder')}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sport Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.sportLabel')} *
              </label>
              <select
                name="sport"
                value={formData.sport}
                onChange={handleChange}
                className="input-modern"
                required
              >
                <option value="calcio">⚽ {t('sports.calcio')}</option>
                <option value="padel">🎾 {t('sports.padel')}</option>
              </select>
            </div>

            {/* Match Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.matchTypeLabel')} *
              </label>
              <select
                name="matchType"
                value={formData.matchType}
                onChange={handleChange}
                className="input-modern"
              >
                {sportOptions[formData.sport].types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.dateLabel')} *
              </label>
              <div className="relative gaming-picker-field">
                <CalendarIcon className="absolute left-3 top-3 h-5 w-5 text-sport-primary pointer-events-none z-10" />
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    setFormData(prev => ({
                      ...prev,
                      date: date ? format(date, 'yyyy-MM-dd') : ''
                    }))
                  }}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t('createMatch.datePlaceholder')}
                  minDate={new Date()}
                  required
                  className="input-modern pl-10 gaming-picker-input"
                  calendarClassName="gaming-calendar"
                  popperClassName="gaming-picker-popper"
                  locale={dateLocale}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.timeLabel')} *
              </label>
              <div className="relative gaming-picker-field" ref={timePickerRef}>
                <ClockIcon className="absolute left-3 top-3 h-5 w-5 text-sport-secondary pointer-events-none z-10" />
                <button
                  type="button"
                  onClick={openTimePicker}
                  className="input-modern pl-10 text-left text-gray-900 dark:text-white"
                >
                  <span className={formData.time ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}>
                    {getDisplayTime(formData.time) || t('createMatch.timePlaceholder')}
                  </span>
                </button>

                {isTimePickerOpen && (
                  <div className="gaming-clock-panel">
                    <div className="gaming-clock-header">
                      <span className="text-xs uppercase tracking-wider text-sport-silver/80">{t('createMatch.kickoffTime')}</span>
                      <span className="gaming-clock-preview">{(timeDraft.hour || '--')}:{(timeDraft.minute || '--')}</span>
                    </div>

                    <div>
                      <p className="gaming-clock-label">{t('createMatch.hour')}</p>
                      <div className="gaming-clock-grid hours">
                        {hours.map(hour => (
                          <button
                            key={hour}
                            type="button"
                            onClick={() => setTimeDraft(prev => ({ ...prev, hour }))}
                            className={`gaming-clock-chip ${timeDraft.hour === hour ? 'active' : ''}`}
                          >
                            {hour}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="gaming-clock-label">{t('createMatch.minutes')}</p>
                      <div className="gaming-clock-grid minutes">
                        {minutes.map(minute => (
                          <button
                            key={minute}
                            type="button"
                            onClick={() => setTimeDraft(prev => ({ ...prev, minute }))}
                            className={`gaming-clock-chip ${timeDraft.minute === minute ? 'active' : ''}`}
                          >
                            {minute}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={applyTimeSelection}
                      disabled={!timeDraft.hour || !timeDraft.minute}
                      className="w-full mt-3 btn-gradient disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('createMatch.applyTime')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createMatch.fullAddress')} *
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="input-modern pl-10"
                placeholder={t('createMatch.addressPlaceholder')}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('auth.city')} *
            </label>
            <div className="relative">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleLocationSearchChange}
                onFocus={() => setShowLocationDropdown(locationResults.length > 0)}
                className="input-modern"
                placeholder={t('createMatch.searchLocationPlaceholder')}
                autoComplete="off"
                required
              />
              <button
                type="button"
                onClick={handleGetCoords}
                disabled={gettingCoords}
                className="absolute right-2 top-2 px-2 py-1 bg-sport-primary/20 text-sport-primary rounded hover:bg-sport-primary/30 transition disabled:opacity-50"
                title={t('location.detectLocation')}
              >
                {gettingCoords ? (
                  <div className="w-4 h-4 border-2 border-sport-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <MapPinIcon className="h-4 w-4" />
                )}
              </button>
              
              {/* Location Search Results Dropdown */}
              {showLocationDropdown && locationResults.length > 0 && (
                <div className="location-dropdown absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {locationResults.map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => selectLocation(result)}
                      className="w-full text-left px-4 py-3 hover:bg-sport-primary/10 dark:hover:bg-sport-primary/20 border-b border-gray-100 dark:border-gray-700 last:border-0"
                    >
                      <p className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">
                        {result.name || result.display_name?.split(',')[0]}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.display_name}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              
              {isSearchingLocation && (
                <div className="absolute right-10 top-3">
                  <div className="w-4 h-4 border-2 border-sport-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            {formData.latitude && formData.longitude && (
              <p className="text-xs text-green-600 mt-1">
                {t('location.coordinatesSet')}: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
              </p>
            )}
          </div>

          {/* Match Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.durationLabel')}
              </label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="input-modern"
              >
                <option value="60">{t('createMatch.duration60')}</option>
                <option value="90">{t('createMatch.duration90')}</option>
                <option value="120">{t('createMatch.duration120')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('createMatch.contributionLabel')}
              </label>
              <div className="relative">
                <CurrencyEuroIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="input-modern pl-10 no-spinner"
                  placeholder={t('createMatch.contributionPlaceholder')}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createMatch.equipmentLabel')}
            </label>
            <input
              type="text"
              name="equipment"
              value={formData.equipment}
              onChange={handleChange}
              className="input-modern"
              placeholder={t('createMatch.equipmentPlaceholder')}
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createMatch.descriptionLabel')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="input-modern"
              placeholder={t('createMatch.descriptionPlaceholder')}
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-semibold mb-1">{t('createMatch.importantNote')}</p>
              <p>{t('createMatch.organizerNotice')}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              {t('createMatch.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-gradient"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('createMatch.loading')}
                </div>
              ) : (
                t('createMatch.createButton')
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default CreateMatch